import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import {
    GraduationCap,
    Plus,
    Filter,
    Search,
    Calendar as CalendarIcon
} from 'lucide-react';
import { SkeletonDashboard } from '../../ui/Skeleton';
import IOSModal from '../../ui/IOSModal';
import CoursesList from './CoursesList';
import CreateCourseModal from './CreateCourseModal';
import { useNavigate } from 'react-router-dom';
import { gymDarkHeaderBar } from '../gymPageTheme';

function computeCourseStats(coursesData) {
    return {
        totalCourses: coursesData.length,
        activeCourses: coursesData.filter((c) => c.status === 'active').length,
        totalSessions: coursesData.reduce((sum, c) => sum + parseInt(c.total_sessions || 0, 10), 0),
        upcomingSessions: coursesData.reduce((sum, c) => sum + parseInt(c.upcoming_sessions || 0, 10), 0),
    };
}

export default function CoursesManager() {
    const { getAuthenticatedApi, isTrainer } = useAuth();
    const { isDark } = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Skeleton solo al primo mount: create / update / delete / ricorrenze non smontano la pagina
    const [initialLoading, setInitialLoading] = useState(true);
    const [courses, setCourses] = useState([]);
    const [error, setError] = useState(null);

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [modalOrigin, setModalOrigin] = useState({ x: 0, y: 0 });
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');

    const [stats, setStats] = useState({
        totalCourses: 0,
        activeCourses: 0,
        totalSessions: 0,
        upcomingSessions: 0
    });

    const canManageCourses = !isTrainer();

    const applyCoursesData = useCallback((coursesData) => {
        setCourses(coursesData);
        setStats(computeCourseStats(coursesData));
    }, []);

    const fetchData = useCallback(async () => {
        try {
            const api = getAuthenticatedApi();
            const coursesResponse = await api.get('/gym_courses.php?action=list_courses');
            const coursesData = coursesResponse.data.courses || [];
            applyCoursesData(coursesData);
            setError(null);
        } catch (err) {
            console.error('Errore caricamento corsi:', err);
            setError('Errore nel caricamento dei corsi');
        } finally {
            setInitialLoading(false);
        }
    }, [getAuthenticatedApi, applyCoursesData]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const refreshCoursesQuietly = useCallback(() => {
        fetchData();
    }, [fetchData]);

    const handleCourseCreated = () => {
        setShowCreateModal(false);
        refreshCoursesQuietly();
    };

    const handleCourseUpdated = () => {
        refreshCoursesQuietly();
    };

    const handleCourseDeleted = (courseId) => {
        setCourses((prev) => {
            const next = prev.filter((c) => Number(c.id) !== Number(courseId));
            setStats(computeCourseStats(next));
            return next;
        });
        refreshCoursesQuietly();
    };

    const filteredCourses = courses.filter((course) => {
        const matchesSearch =
            course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (course.description && course.description.toLowerCase().includes(searchQuery.toLowerCase()));
        const matchesCategory = categoryFilter === 'all' || course.category === categoryFilter;
        return matchesSearch && matchesCategory;
    });

    const categories = ['all', ...new Set(courses.map((c) => c.category).filter(Boolean))];

    if (initialLoading) {
        return (
            <div className="min-h-screen bg-gray-50 p-6 dark:bg-gray-950">
                <SkeletonDashboard />
            </div>
        );
    }

    return (
        <div className="flex h-screen flex-col overflow-hidden bg-gray-50 dark:bg-gray-950">
            <div className={`flex-shrink-0 ${isDark ? gymDarkHeaderBar : ''}`}>
                <div className="mx-auto max-w-7xl p-6">
                    <div className="mb-8">
                        <div className="mb-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <GraduationCap className="text-indigo-600 dark:text-gray-300" size={32} />
                                <div>
                                    <h1 className="text-3xl font-bold text-gray-900 dark:font-open-sans dark:text-white">
                                        {t('gym.courses.title')}
                                    </h1>
                                    <p className="text-gray-600 dark:text-white/75">
                                        {t('gym.courses.description')}
                                    </p>
                                </div>
                            </div>
                            {canManageCourses && (
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        if (!canManageCourses) return;
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setModalOrigin({
                                            x: rect.left + rect.width / 2,
                                            y: rect.top + rect.height / 2
                                        });
                                        setShowCreateModal(true);
                                    }}
                                    className="flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-white transition-colors hover:bg-indigo-700 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110"
                                >
                                    <Plus className="h-5 w-5" />
                                    {t('gym.courses.newCourse')}
                                </button>
                            )}
                        </div>

                        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                            <StatCard
                                title={t('gym.courses.stats.totalCourses')}
                                value={stats.totalCourses}
                                icon="📚"
                                color="blue"
                                index={0}
                            />
                            <StatCard
                                title={t('gym.courses.stats.activeCourses')}
                                value={stats.activeCourses}
                                icon="✅"
                                color="green"
                                index={1}
                            />
                            <StatCard
                                title={t('gym.courses.stats.totalSessions')}
                                value={stats.totalSessions}
                                icon="📅"
                                color="purple"
                                index={2}
                            />
                            <StatCard
                                title={t('gym.courses.stats.upcomingSessions')}
                                value={stats.upcomingSessions}
                                icon="🚀"
                                color="orange"
                                index={3}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto">
                <div className="mx-auto max-w-7xl p-6">
                    <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow dark:border-white/10 dark:bg-gray-900">
                        <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
                            <div className="max-w-md flex-1">
                                <div className="relative">
                                    <Search
                                        className="absolute left-3 top-1/2 -translate-y-1/2 transform text-gray-400 dark:text-white/45"
                                        size={20}
                                    />
                                    <input
                                        type="text"
                                        placeholder={t('gym.courses.filters.searchPlaceholder')}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder:text-white/50 dark:focus:border-indigo-500 dark:focus:ring-white/30"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <Filter size={20} className="text-gray-500 dark:text-white/55" />
                                <select
                                    value={categoryFilter}
                                    onChange={(e) => setCategoryFilter(e.target.value)}
                                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:ring-2 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:focus:ring-white/30"
                                >
                                    {categories.map((cat) => (
                                        <option key={cat} value={cat}>
                                            {cat === 'all' ? t('gym.courses.filters.allCategories') : cat}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <button
                                type="button"
                                onClick={() => navigate('/gym/calendar')}
                                className="flex items-center gap-2 rounded-lg bg-indigo-100 px-4 py-2 text-indigo-700 transition-colors hover:bg-indigo-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:brightness-110"
                            >
                                <CalendarIcon size={18} />
                                {t('gym.courses.viewCalendar')}
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                            <p className="text-red-700 dark:text-red-400">{error}</p>
                        </div>
                    )}

                    <CoursesList
                        courses={filteredCourses}
                        onCourseUpdated={handleCourseUpdated}
                        onCourseDeleted={handleCourseDeleted}
                        readOnly={!canManageCourses}
                    />

                    {filteredCourses.length === 0 && (
                        <div className="rounded-lg border border-gray-200 bg-white p-12 text-center shadow dark:border-white/10 dark:bg-gray-900">
                            <GraduationCap className="mx-auto mb-4 text-gray-400 dark:text-white/40" size={64} />
                            <h3 className="mb-2 text-xl font-semibold text-gray-900 dark:text-white">
                                {searchQuery || categoryFilter !== 'all'
                                    ? t('gym.courses.emptyState.noCoursesFound')
                                    : t('gym.courses.emptyState.noCourses')}
                            </h3>
                            <p className="mb-6 text-gray-600 dark:text-white/70">
                                {searchQuery || categoryFilter !== 'all'
                                    ? t('gym.courses.emptyState.noCoursesFoundDescription')
                                    : t('gym.courses.emptyState.noCoursesDescription')}
                            </p>
                            {!searchQuery && categoryFilter === 'all' && canManageCourses && (
                                <button
                                    type="button"
                                    onClick={() => setShowCreateModal(true)}
                                    className="rounded-lg bg-indigo-600 px-6 py-3 text-white transition-colors hover:bg-indigo-700 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110"
                                >
                                    {t('gym.courses.emptyState.createFirst')}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {canManageCourses && (
                <IOSModal
                    isOpen={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    origin={modalOrigin}
                    size="full"
                    className="!h-[96vh] !max-h-[96vh]"
                    title={t('gym.courses.newCourse')}
                >
                    <CreateCourseModal
                        isOpen={showCreateModal}
                        onClose={() => setShowCreateModal(false)}
                        onSuccess={handleCourseCreated}
                    />
                </IOSModal>
            )}
        </div>
    );
}

function StatCard({ title, value, icon, color, index = 0 }) {
    const colorClasses = {
        blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
        green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
        purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
        orange: 'bg-orange-50 dark:bg-orange-900/20 text-orange-600 dark:text-orange-400'
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ scale: 1.05 }}
            className={`
                ${colorClasses[color]}
                cursor-pointer rounded-lg p-4
                transition-all duration-300
                hover:shadow-lg
            `}
        >
            <div className="flex items-center justify-between">
                <div>
                    <p className="mb-1 text-sm opacity-80">{title}</p>
                    <p className="text-2xl font-bold">
                        <CountUp end={value} duration={0.6} preserveValue separator="," />
                    </p>
                </div>
                <div className="text-3xl">{icon}</div>
            </div>
        </motion.div>
    );
}
